import type { Writable } from 'svelte/store';
import type { Snippet } from 'svelte';
import type { Column, Columns, FlatColumns, TableRow } from '#lib/zod/output/TabularData.js';
import type { DeepReadonly } from '#lib/utils/type.js';
import type { ContextKey } from '#lib/utils/svelte-context.js';
import type { downloadIterable } from '#lib/utils/download.js';
import type { TableDownloadOptions } from './utils';
import type { TableState } from './state.svelte';

export type RowNameFunction = Snippet<[row: TableRow, level: number]>;

export type CountHintType = 'subgroups' | 'leafs' | 'both' | undefined;

export type TooltipStore = Writable<
  | {
      text: string;
      width?: string;
    }
  | { snippet: Snippet<[TableRow, Column]>; props: [TableRow, Column] }
  | undefined
>;

export type DownloadFunction = (
  filename: string,
  options: TableDownloadOptions,
) => ReturnType<typeof downloadIterable>;

// ─── Context ──────────────────────────────────────────────────

export type RowSettings = {
  readonly nameFunction?: RowNameFunction;
  readonly hidePeak: boolean;
  readonly countHint: CountHintType;
  readonly countHintTooltip?: string | { level: number; text: string | undefined }[];
  readonly showGroupHeaderAlerts: boolean;
  readonly rowNameBold: boolean;
  readonly onRowClick?: (row: TableRow) => void;
};

export type DataTableContext = {
  readonly tableState: TableState;
  readonly rows: TableRow[];
  readonly cols: DeepReadonly<Columns>;
  readonly flatCols: FlatColumns;
  readonly borders: Set<number>;
  readonly hasAlerts: boolean;
  readonly showCheckboxes: boolean;
  readonly tooltip: TooltipStore;
  readonly rowSettings: RowSettings;
};

export const DataTableContextKey: ContextKey<DataTableContext> = {};
